import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LuSearch, LuUser, LuBuilding2, LuLoader2 } from 'react-icons/lu';
import lobbyService from '../../features/lobby/lobbyService';
import { Input } from '../ui';

const MIN_CHARS = 2;

// Server-side typeahead over lobbyists and the clients they represent in the current state.
const LobbySearchBox = ({ autoFocus = false }) => {
  const { state } = useParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState({ lobbyists: [], companies: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) {
      setResults({ lobbyists: [], companies: [] });
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await lobbyService.searchLobby(q, controller.signal);
        setResults(data);
        setError(false);
        setHighlight(0);
      } catch (err) {
        if (err.name !== 'CanceledError') setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const items = [
    ...results.lobbyists.map((l) => ({
      key: `l-${l.LobbyistID}`,
      to: `/${state}/lobbyist/${l.LobbyistID}`,
      label: [l.LobbyistFirstName, l.LobbyistLastName].filter(Boolean).join(' '),
      sub: l.LobbyistBusiness,
      type: 'lobbyist',
    })),
    ...results.companies.map((c) => ({
      key: `c-${c.CompanyID}`,
      to: `/${state}/company/${c.CompanyID}`,
      label: c.CompanyName,
      type: 'company',
    })),
  ];

  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      navigate(items[highlight].to);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showPanel = open && term.trim().length >= MIN_CHARS;
  const renderGroup = (type, title, Icon) => {
    const group = items.filter((i) => i.type === type);
    if (!group.length) return null;
    return (
      <div className="py-1">
        <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        {group.map((item) => {
          const index = items.indexOf(item);
          return (
            <Link
              key={item.key}
              to={item.to}
              role="option"
              aria-selected={index === highlight}
              onMouseEnter={() => setHighlight(index)}
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${
                index === highlight ? 'bg-muted text-foreground' : 'text-foreground'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate font-medium">{item.label}</span>
              {item.sub && <span className="ml-auto truncate text-xs text-muted-foreground">{item.sub}</span>}
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <LuSearch className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <Input
        type="search"
        value={term}
        autoFocus={autoFocus}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search lobbyists, firms, or clients…"
        aria-label="Search lobbyists and clients"
        role="combobox"
        aria-expanded={showPanel}
        className="pl-9 pr-9"
      />
      {loading && (
        <LuLoader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
      )}
      {showPanel && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-12 z-30 max-h-[60vh] overflow-y-auto rounded-lg border border-border bg-card p-1 shadow-lg"
        >
          {error ? (
            <p className="px-3 py-3 text-sm text-danger">Search failed. Try again.</p>
          ) : items.length ? (
            <>
              {renderGroup('lobbyist', 'Lobbyists', LuUser)}
              {renderGroup('company', 'Clients', LuBuilding2)}
            </>
          ) : (
            !loading && <p className="px-3 py-3 text-sm text-muted-foreground">No matches for “{term.trim()}”.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default LobbySearchBox;
